import { sectionStyles, hexToRgba } from '../utils';

const SIZES = { sm: 32, md: 64, lg: 128 };

export default function SeparatorSection({ block, primaryColor, secondaryColor }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const accent = s.accent || primaryColor || '#6366F1';
    const size = SIZES[config.size] || 64;
    const style = config.style || 'space';
    const bg = config.bg_color || 'transparent';
    const width = config.max_width || 'full'; // full, lg, md, sm
    const widthClass = width === 'full' ? 'w-full' : width === 'lg' ? 'max-w-4xl mx-auto' : width === 'md' ? 'max-w-2xl mx-auto' : 'max-w-sm mx-auto';

    const wrap = (children, extraClass = '') => (
        <div className={`relative overflow-hidden ${extraClass}`} style={{ backgroundColor: bg === 'transparent' ? undefined : bg, height: ['wave','curve','zigzag','scallop','mountains','tilt'].includes(style) ? `${size}px` : undefined }}>
            {children}
        </div>
    );

    // ── Space ──
    if (style === 'space') return <div style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }} />;

    // ── Line ──
    if (style === 'line') return (
        <div className="flex items-center justify-center px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className={widthClass + ' w-full'}><hr className="border-gray-200" /></div>
        </div>
    );

    // ── Dots ──
    if (style === 'dots') return (
        <div className="flex items-center justify-center gap-2" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            {[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: hexToRgba(accent, 0.4) }} />)}
        </div>
    );

    // ── Gradient Line ──
    if (style === 'gradient') return (
        <div className="flex items-center justify-center px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className={widthClass + ' w-full h-px'} style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
        </div>
    );

    // ── Fade ──
    if (style === 'fade') return (
        <div className="flex items-center justify-center px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className={widthClass + ' w-full'}>
                <div className="h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${hexToRgba(accent, 0.3)} 20%, ${hexToRgba(accent, 0.5)} 50%, ${hexToRgba(accent, 0.3)} 80%, transparent 100%)` }} />
            </div>
        </div>
    );

    // ── Diamond ──
    if (style === 'diamond') return (
        <div className="flex items-center justify-center gap-4 px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${hexToRgba(accent, 0.3)})`, maxWidth: '200px', marginLeft: 'auto' }} />
            <div className="w-2.5 h-2.5 rotate-45" style={{ backgroundColor: accent, opacity: 0.6 }} />
            <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${hexToRgba(accent, 0.3)}, transparent)`, maxWidth: '200px', marginRight: 'auto' }} />
        </div>
    );

    // ── Ornament ──
    if (style === 'ornament') return (
        <div className="flex items-center justify-center gap-3 px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${hexToRgba(accent, 0.25)})`, maxWidth: '180px', marginLeft: 'auto' }} />
            <svg width="60" height="20" viewBox="0 0 60 20" fill="none">
                <path d="M0 10 Q10 0 15 10 Q20 20 30 10 Q40 0 45 10 Q50 20 60 10" stroke={hexToRgba(accent, 0.4)} strokeWidth="1.5" fill="none"/>
                <circle cx="30" cy="10" r="3" fill={hexToRgba(accent, 0.3)} />
            </svg>
            <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${hexToRgba(accent, 0.25)}, transparent)`, maxWidth: '180px', marginRight: 'auto' }} />
        </div>
    );

    // ── Glow ──
    if (style === 'glow') return (
        <div className="flex items-center justify-center px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className={widthClass + ' w-full relative'}>
                <div className="h-px" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
                <div className="h-4 -mt-2" style={{ background: `linear-gradient(90deg, transparent, ${hexToRgba(accent, 0.15)}, transparent)`, filter: 'blur(8px)' }} />
            </div>
        </div>
    );

    // ── Icon ──
    if (style === 'icon') {
        const icon = content.icon || '✦';
        return (
            <div className="flex items-center justify-center gap-4 px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
                <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${hexToRgba(accent, 0.3)})`, maxWidth: '250px', marginLeft: 'auto' }} />
                <span className="text-xl" style={{ color: hexToRgba(accent, 0.5) }}>{icon}</span>
                <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${hexToRgba(accent, 0.3)}, transparent)`, maxWidth: '250px', marginRight: 'auto' }} />
            </div>
        );
    }

    // ── Slash ──
    if (style === 'slash') return (
        <div className="flex items-center justify-center" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className="flex gap-1.5">
                {[0,1,2,3,4].map(i => (
                    <div key={i} className="w-px h-4" style={{ backgroundColor: hexToRgba(accent, 0.2 + i * 0.08), transform: 'rotate(20deg)' }} />
                ))}
            </div>
        </div>
    );

    // ── Wave ──
    if (style === 'wave') return wrap(
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <path d="M0,50 C240,100 480,0 720,50 C960,100 1200,0 1440,50 L1440,100 L0,100 Z" fill={hexToRgba(accent, 0.06)} />
            <path d="M0,60 C360,10 720,90 1080,40 C1260,20 1380,50 1440,45 L1440,100 L0,100 Z" fill={hexToRgba(accent, 0.04)} />
        </svg>
    );

    // ── Curve ──
    if (style === 'curve') return wrap(
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <path d="M0,80 Q720,0 1440,80 L1440,100 L0,100 Z" fill={hexToRgba(accent, 0.06)} />
        </svg>
    );

    // ── Zigzag ──
    if (style === 'zigzag') {
        const points = Array.from({length: 37}, (_, i) => `${i * 40},${i % 2 === 0 ? 0 : 30}`).join(' ');
        return wrap(
            <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                <polyline points={points} fill="none" stroke={hexToRgba(accent, 0.15)} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                <path d={`M0,30 ${Array.from({length: 37}, (_, i) => `L${i * 40},${i % 2 === 0 ? 0 : 30}`).join(' ')} L1440,100 L0,100 Z`} fill={hexToRgba(accent, 0.03)} />
            </svg>
        );
    }

    // ── Scallop ──
    if (style === 'scallop') {
        const count = 20;
        const w = 1440 / count;
        const circles = Array.from({length: count}, (_, i) => `<circle cx="${w * i + w/2}" cy="0" r="${w/2}" />`).join('');
        return wrap(
            <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                <defs><clipPath id="scallop">{Array.from({length: count}, (_, i) => <circle key={i} cx={w * i + w/2} cy={0} r={w/2} />)}</clipPath></defs>
                <rect x="0" y="0" width="1440" height="50" fill={hexToRgba(accent, 0.06)} clipPath="url(#scallop)" />
                <line x1="0" y1="0" x2="1440" y2="0" stroke={hexToRgba(accent, 0.1)} strokeWidth="1" />
            </svg>
        );
    }

    // ── Mountains ──
    if (style === 'mountains') return wrap(
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <path d="M0,100 L200,30 L400,70 L600,15 L800,60 L1000,25 L1200,55 L1440,20 L1440,100 Z" fill={hexToRgba(accent, 0.05)} />
            <path d="M0,100 L150,50 L350,80 L550,35 L750,70 L950,40 L1150,65 L1350,30 L1440,45 L1440,100 Z" fill={hexToRgba(accent, 0.03)} />
        </svg>
    );

    // ── Tilt / Diagonal ──
    if (style === 'tilt') return wrap(
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <polygon points="0,100 1440,0 1440,100" fill={hexToRgba(accent, 0.05)} />
        </svg>
    );

    // ── Double Line ──
    if (style === 'double') return (
        <div className="flex flex-col items-center justify-center gap-1.5 px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className={widthClass + ' w-full h-px'} style={{ backgroundColor: hexToRgba(accent, 0.15) }} />
            <div className={widthClass + ' w-full h-px'} style={{ backgroundColor: hexToRgba(accent, 0.08) }} />
        </div>
    );

    // ── Dashed ──
    if (style === 'dashed') return (
        <div className="flex items-center justify-center px-4" style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }}>
            <div className={widthClass + ' w-full h-px'} style={{ backgroundImage: `repeating-linear-gradient(90deg, ${hexToRgba(accent, 0.3)} 0, ${hexToRgba(accent, 0.3)} 8px, transparent 8px, transparent 16px)` }} />
        </div>
    );

    // Fallback: space
    return <div style={{ height: `${size}px`, backgroundColor: bg === 'transparent' ? undefined : bg }} />;
}
